import React, { useReducer, Dispatch } from 'react';
import { CourseType, OnlineCourseStoredType, OnlineMockExamCourseStoredType, PersonalCourseStoredType, PersonalTestStoredType } from '../services/types';
import { sessionStore } from '../services/utils';

interface State {
    savedPersonalCourses: PersonalCourseStoredType[];
    savedOnlineCourses: OnlineCourseStoredType[];
    savedOnlineMockExamCourses: OnlineMockExamCourseStoredType[];
    savedPersonalTests: PersonalTestStoredType[];
}

enum CoursesTypes {
    SaveCourse = 'SAVE_COURSE',
    RemoveSavedCourse = 'REMOVE_SAVED_COURSE',
    ResetSavedCourses = 'RESET_SAVED_COURSES',
}

type Actions =
    | {
          type: CoursesTypes.SaveCourse;
          course: PersonalCourseStoredType | OnlineCourseStoredType | OnlineMockExamCourseStoredType | PersonalTestStoredType;
          courseType: CourseType;
      }
    | { type: CoursesTypes.RemoveSavedCourse; id: number; courseType: CourseType }
    | { type: CoursesTypes.ResetSavedCourses; courseType?: CourseType }

const initialState = {
    savedPersonalCourses: sessionStore.get('onsite_courses') || [],
    savedOnlineCourses: sessionStore.get('online_courses') || [],
    savedOnlineMockExamCourses: sessionStore.get('online_mock_exam_courses') || [],
    savedPersonalTests: sessionStore.get('onsite_tests') || [],
};

function reducer(state: State, action: Actions) {
    const courseType = {
        'personal': 'savedPersonalCourses',
        'online': 'savedOnlineCourses',
        'online-mock-exam': 'savedOnlineMockExamCourses',
        'personalTest': 'savedPersonalTests',
    };
    switch (action.type) {
        case CoursesTypes.SaveCourse:
            let finalSavedArray;
            if (action.courseType === 'online') {
                if (state.savedOnlineCourses.filter((course) => course.id === action.course.id)[0]) {
                    finalSavedArray = state.savedOnlineCourses.map((course) => {
                        if (course.id === action.course.id) {
                            return action.course;
                        } else {
                            return course;
                        }
                    });
                } else {
                    finalSavedArray = [...state.savedOnlineCourses, action.course];
                }
            } else if (action.courseType === 'online-mock-exam') {
                if (state.savedOnlineMockExamCourses.filter((course) => course.id === action.course.id)[0]) {
                    finalSavedArray = state.savedOnlineMockExamCourses.map((course) => {
                        if (course.id === action.course.id) {
                            return action.course;
                        } else {
                            return course;
                        }
                    });
                } else {
                    finalSavedArray = [...state.savedOnlineMockExamCourses, action.course];
                }
            } else if (action.courseType === 'personal') {
                finalSavedArray = [...state.savedPersonalCourses, action.course];
            } else if (action.courseType === 'personalTest') {
                finalSavedArray = [...state.savedPersonalTests, action.course];
            }
            return {
                ...state,
                [courseType[action.courseType]]: finalSavedArray,
            };
        case CoursesTypes.RemoveSavedCourse:
            let finalArray;
            if (action.courseType === 'online') {
                finalArray = state.savedOnlineCourses.filter((course) => course.id !== action.id);
            } else if (action.courseType === 'online-mock-exam') {
                finalArray = state.savedOnlineMockExamCourses.filter((course) => course.id !== action.id);
            } else if (action.courseType === 'personal') {
                finalArray = state.savedPersonalCourses.filter((course) => course.id !== action.id);
            } else if (action.courseType === 'personalTest') {
                finalArray = state.savedPersonalTests.filter((course) => course.id !== action.id);
            }
            return {
                ...state,
                [courseType[action.courseType]]: finalArray,
            };
        case CoursesTypes.ResetSavedCourses:
            if (action.courseType) {
                return {
                    ...state,
                    [courseType[action.courseType]]: [],
                };
            }
            return { ...state, savedPersonalCourses: [], savedOnlineCourses: [], savedOnlineMockExamCourses: [], savedPersonalTests: [] };
        default:
            throw new Error('This action type is not exist in Courses context');
    }
}

const CoursesProvider: React.FC = ({ children }) => {
    const [state, dispatch] = useReducer(reducer, initialState);

    return <AppContext.Provider value={{ state, dispatch }}>{children}</AppContext.Provider>;
};

const AppContext = React.createContext<{
    state: State;
    dispatch: Dispatch<Actions>;
}>({
    state: initialState,
    dispatch: () => null,
});

function useCoursesState() {
    const context = React.useContext(AppContext);
    if (context === undefined) {
        throw new Error('useCoursesState must be used within a CoursesProvider');
    }
    return context;
}

export { CoursesProvider, useCoursesState, CoursesTypes };
